import { useEffect, useRef, useState } from 'react';
import type { Manager, Socket } from 'socket.io-client';
import { createSocketManager } from '@/lib/socket';
import type { SaleStatus, SocketConfig } from '@/types/sale';

interface LatestStatusPayload {
  status: string;
  data?: {
    status?: SaleStatus;
    paymentTransactionStatus?: string | null;
    invoiceId?: string | null;
  };
}

interface UseMidtransSocketArgs {
  config: SocketConfig | null;
  open: boolean;
}

interface UseMidtransSocketReturn {
  /** Latest status the server pushed, or null if it has not spoken yet. */
  status: SaleStatus | null;
  /**
   * The gateway's own word alongside it (`settlement`, `expire`, `deny`…).
   *
   * `status` flattens every unhappy ending into FAILED, so this is what lets
   * the panel say "the code ran out of time" instead of "declined".
   */
  paymentTransactionStatus: string | null;
  /** False while the channel is unusable — the panel leans on its poll then. */
  connected: boolean;
}

/**
 * Watch a QRIS payment's `/midtrans` channel.
 *
 * This is a RELAY, not an observer. Unlike the cashless and EDC channels — which
 * carry what a device on our own LAN is doing — nothing here sees the payment.
 * The server pushes only what Midtrans has told it, via the webhook or its own
 * poller. So a silent socket does not mean "not paid", and the panel keeps a
 * slow poll running underneath for exactly that reason: if the webhook cannot
 * reach the server, no socket can invent the payment.
 *
 * Narrower than useEdcSocket by nature: a QR has no intermediate states to
 * report. It is pending until the gateway says otherwise.
 */
export function useMidtransSocket({
  config,
  open,
}: UseMidtransSocketArgs): UseMidtransSocketReturn {
  const [status, setStatus] = useState<SaleStatus | null>(null);
  const [gatewayStatus, setGatewayStatus] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const managerRef = useRef<Manager | null>(null);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!open || !config?.url || !config?.token || !config?.namespace) return;

    setStatus(null);
    setGatewayStatus(null);
    setConnected(false);

    const manager = createSocketManager(config.url, { forceNew: false });
    managerRef.current = manager;
    const socket = manager.socket(config.namespace, { auth: { token: config.token } });
    socketRef.current = socket;

    socket.on('connect', () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));

    socket.on('latest_status', (raw: string) => {
      try {
        const payload: LatestStatusPayload = typeof raw === 'string' ? JSON.parse(raw) : raw;
        // `resumed` is the server catching a late or reconnecting client up on
        // a payment that may already be finished; it carries the same shape.
        const next = payload.data?.status;
        if (next) setStatus(next);
        if (payload.data?.paymentTransactionStatus) {
          setGatewayStatus(payload.data.paymentTransactionStatus);
        }
      } catch {
        /* malformed payload — the poll still covers us */
      }
    });

    socket.on('connect_error', () => {
      // Not a payment signal. The backend keeps driving the sale to a
      // conclusion either way, so this only downgrades us to polling.
      setConnected(false);
    });

    return () => {
      try {
        socket.removeAllListeners();
        socket.disconnect();
      } catch {
        /* ignore */
      }
      socketRef.current = null;
      managerRef.current = null;
    };
  }, [open, config?.url, config?.token, config?.namespace]);

  return { status, paymentTransactionStatus: gatewayStatus, connected };
}
